import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { AppState, CanvasElement, ElementId, ElementType } from '@extapia/shared';
import { boundsOf, indexBetween, midIndex, newId, newNonce, newSeed, now } from '@extapia/shared';

const DEFAULT_APP_STATE: AppState = {
  viewBackgroundColor: '#ffffff',
  gridSize: null,
  scrollX: 0,
  scrollY: 0,
  zoom: 1,
  theme: 'light',
  currentTool: 'selection',
  activeStrokeColor: '#111827',
  activeFillColor: 'transparent',
  activeFillStyle: 'hachure',
  activeStrokeWidth: 2,
  activeStrokeStyle: 'solid',
  activeRoughness: 1,
  activeOpacity: 100,
  activeFontFamily: 1,
  activeFontSize: 20,
};

interface HistoryEntry {
  elements: CanvasElement[];
}

type ActiveStylePatch = Partial<
  Pick<
    AppState,
    | 'activeStrokeColor'
    | 'activeFillColor'
    | 'activeStrokeWidth'
    | 'activeRoughness'
    | 'activeOpacity'
    | 'activeFillStyle'
    | 'activeStrokeStyle'
    | 'activeFontFamily'
    | 'activeFontSize'
  >
>;

interface CanvasStore {
  elements: CanvasElement[];
  selectedIds: Set<ElementId>;
  app: AppState;
  history: HistoryEntry[];
  historyIndex: number;
  clipboard: CanvasElement[] | null;
  editingTextId: ElementId | null;

  setTool: (tool: AppState['currentTool']) => void;
  setBackground: (color: string) => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setGrid: (size: number | null) => void;
  setActiveStyle: (patch: ActiveStylePatch) => void;

  addElement: (el: CanvasElement) => void;
  addElements: (els: CanvasElement[]) => void;
  updateElement: (id: ElementId, patch: Partial<CanvasElement>) => void;
  deleteElement: (id: ElementId) => void;
  deleteSelected: () => void;
  selectOnly: (ids: ElementId[]) => void;
  selectAdd: (id: ElementId) => void;
  clearSelection: () => void;
  selectAll: () => void;

  // Clipboard + duplication
  copy: () => void;
  cut: () => void;
  paste: (atWorld?: { x: number; y: number }) => void;
  duplicateSelected: () => void;

  // Layer / z-order
  bringToFront: () => void;
  sendToBack: () => void;
  bringForward: () => void;
  sendBackward: () => void;

  // Align / distribute
  align: (edge: 'left' | 'right' | 'top' | 'bottom' | 'centerX' | 'centerY') => void;
  distribute: (axis: 'horizontal' | 'vertical') => void;

  // Text edit overlay
  startTextEdit: (id: ElementId) => void;
  stopTextEdit: () => void;

  setViewport: (patch: Partial<Pick<AppState, 'scrollX' | 'scrollY' | 'zoom'>>) => void;

  commit: () => void;
  undo: () => void;
  redo: () => void;

  loadFromJSON: (json: { elements: CanvasElement[]; appState?: Partial<AppState> }) => void;
  toJSON: () => { elements: CanvasElement[]; appState: AppState };
}

const HISTORY_LIMIT = 100;

// Sort by fractional index. Higher = drawn later = on top.
const byIndex = (a: CanvasElement, b: CanvasElement): number => a.index.localeCompare(b.index);

export const useCanvas = create<CanvasStore>()(
  subscribeWithSelector(
    immer((set, get) => ({
      elements: [],
      selectedIds: new Set<ElementId>(),
      app: DEFAULT_APP_STATE,
      history: [{ elements: [] }],
      historyIndex: 0,
      clipboard: null,
      editingTextId: null,

      setTool: (tool) =>
        set((s) => {
          s.app.currentTool = tool;
          if (tool !== 'selection') s.selectedIds = new Set();
          if (tool !== 'text') s.editingTextId = null;
        }),

      setBackground: (color) =>
        set((s) => {
          s.app.viewBackgroundColor = color;
        }),

      setTheme: (theme) =>
        set((s) => {
          s.app.theme = theme;
          s.app.viewBackgroundColor = theme === 'dark' ? '#0b1220' : '#ffffff';
        }),

      setGrid: (size) =>
        set((s) => {
          s.app.gridSize = size;
        }),

      setActiveStyle: (patch) =>
        set((s) => {
          Object.assign(s.app, patch);
        }),

      addElement: (el) =>
        set((s) => {
          s.elements.push(el);
        }),

      addElements: (els) =>
        set((s) => {
          s.elements.push(...els);
        }),

      updateElement: (id, patch) =>
        set((s) => {
          const idx = s.elements.findIndex((e) => e.id === id);
          if (idx === -1) return;
          Object.assign(s.elements[idx]!, patch);
          s.elements[idx]!.version += 1;
          s.elements[idx]!.versionNonce = newNonce();
          s.elements[idx]!.updated = now();
        }),

      deleteElement: (id) =>
        set((s) => {
          const el = s.elements.find((e) => e.id === id);
          if (el) el.isDeleted = true;
          s.selectedIds.delete(id);
        }),

      deleteSelected: () =>
        set((s) => {
          for (const el of s.elements) if (s.selectedIds.has(el.id)) el.isDeleted = true;
          s.selectedIds = new Set();
        }),

      selectOnly: (ids) =>
        set((s) => {
          s.selectedIds = new Set(ids);
        }),

      selectAdd: (id) =>
        set((s) => {
          s.selectedIds.add(id);
        }),

      clearSelection: () =>
        set((s) => {
          s.selectedIds = new Set();
        }),

      selectAll: () =>
        set((s) => {
          s.selectedIds = new Set(s.elements.filter((e) => !e.isDeleted).map((e) => e.id));
        }),

      // ---- Clipboard --------------------------------------------------

      copy: () => {
        const st = get();
        const selected = st.elements.filter((e) => st.selectedIds.has(e.id) && !e.isDeleted);
        if (selected.length === 0) return;
        set((s) => {
          s.clipboard = selected.map((e) => ({ ...e }));
        });
      },

      cut: () => {
        get().copy();
        set((s) => {
          for (const el of s.elements) if (s.selectedIds.has(el.id)) el.isDeleted = true;
          s.selectedIds = new Set();
        });
        get().commit();
      },

      paste: (atWorld) => {
        const st = get();
        if (!st.clipboard || st.clipboard.length === 0) return;
        // Offset paste so it doesn't overlap the original perfectly.
        const dx = atWorld ? atWorld.x - st.clipboard[0]!.x : 20;
        const dy = atWorld ? atWorld.y - st.clipboard[0]!.y : 20;
        const idMap = new Map<ElementId, ElementId>();
        const clones: CanvasElement[] = st.clipboard.map((e) => {
          const newElId = newId();
          idMap.set(e.id, newElId);
          return { ...e, id: newElId, x: e.x + dx, y: e.y + dy, seed: newSeed(), versionNonce: newNonce() };
        });
        // rewire groupIds & bound refs (best-effort)
        for (const c of clones) {
          c.groupIds = c.groupIds.map((g) => idMap.get(g) ?? g);
          c.boundElements = c.boundElements.map((b) => ({
            id: idMap.get(b.id) ?? b.id,
            type: b.type,
          }));
        }
        // assign new fractional indices at the top of z-order
        const alive = st.elements.filter((e) => !e.isDeleted);
        let last = alive.length > 0 ? alive[alive.length - 1]!.index : null;
        for (const c of clones) {
          c.index = indexBetween(last, null);
          last = c.index;
        }
        set((s) => {
          s.elements.push(...clones);
          s.selectedIds = new Set(clones.map((c) => c.id));
        });
        get().commit();
      },

      duplicateSelected: () => {
        get().copy();
        get().paste();
      },

      // ---- Layer / z-order --------------------------------------------

      bringToFront: () =>
        set((s) => {
          const alive = s.elements.filter((e) => !e.isDeleted);
          const sorted = [...alive].sort(byIndex);
          let last = sorted.length > 0 ? sorted[sorted.length - 1]!.index : null;
          for (const el of s.elements) {
            if (s.selectedIds.has(el.id)) {
              el.index = indexBetween(last, null);
              last = el.index;
            }
          }
        }),

      sendToBack: () =>
        set((s) => {
          const alive = s.elements.filter((e) => !e.isDeleted && !s.selectedIds.has(e.id));
          const sorted = [...alive].sort(byIndex);
          let first = sorted.length > 0 ? sorted[0]!.index : null;
          // walk in reverse so preserved order among selected
          const targets = s.elements.filter((e) => s.selectedIds.has(e.id));
          for (let i = targets.length - 1; i >= 0; i--) {
            targets[i]!.index = indexBetween(null, first);
            first = targets[i]!.index;
          }
        }),

      bringForward: () =>
        set((s) => {
          const sorted = [...s.elements].filter((e) => !e.isDeleted).sort(byIndex);
          for (let i = 0; i < sorted.length - 1; i++) {
            if (s.selectedIds.has(sorted[i]!.id) && !s.selectedIds.has(sorted[i + 1]!.id)) {
              const nextIdx = sorted[i + 1]!.index;
              const afterNext = sorted[i + 2]?.index ?? null;
              const target = s.elements.find((e) => e.id === sorted[i]!.id);
              if (target) target.index = indexBetween(nextIdx, afterNext);
            }
          }
        }),

      sendBackward: () =>
        set((s) => {
          const sorted = [...s.elements].filter((e) => !e.isDeleted).sort(byIndex);
          for (let i = sorted.length - 1; i > 0; i--) {
            if (s.selectedIds.has(sorted[i]!.id) && !s.selectedIds.has(sorted[i - 1]!.id)) {
              const prevIdx = sorted[i - 1]!.index;
              const beforePrev = sorted[i - 2]?.index ?? null;
              const target = s.elements.find((e) => e.id === sorted[i]!.id);
              if (target) target.index = indexBetween(beforePrev, prevIdx);
            }
          }
        }),

      // ---- Align / distribute -----------------------------------------

      align: (edge) =>
        set((s) => {
          const targets = s.elements.filter((e) => s.selectedIds.has(e.id) && !e.isDeleted);
          if (targets.length < 2) return;
          const bs = targets.map((e) => boundsOf(e));
          const minX = Math.min(...bs.map((b) => b.x1));
          const maxX = Math.max(...bs.map((b) => b.x2));
          const minY = Math.min(...bs.map((b) => b.y1));
          const maxY = Math.max(...bs.map((b) => b.y2));
          const cx = (minX + maxX) / 2;
          const cy = (minY + maxY) / 2;
          for (const el of targets) {
            switch (edge) {
              case 'left':
                el.x = minX;
                break;
              case 'right':
                el.x = maxX - el.width;
                break;
              case 'top':
                el.y = minY;
                break;
              case 'bottom':
                el.y = maxY - el.height;
                break;
              case 'centerX':
                el.x = cx - el.width / 2;
                break;
              case 'centerY':
                el.y = cy - el.height / 2;
                break;
            }
          }
        }),

      distribute: (axis) =>
        set((s) => {
          const targets = s.elements.filter((e) => s.selectedIds.has(e.id) && !e.isDeleted);
          if (targets.length < 3) return;
          if (axis === 'horizontal') {
            const sorted = [...targets].sort((a, b) => a.x - b.x);
            const first = sorted[0]!;
            const last = sorted[sorted.length - 1]!;
            const totalSpan = last.x + last.width - first.x;
            const usedWidth = sorted.reduce((sum, e) => sum + e.width, 0);
            const gap = (totalSpan - usedWidth) / (sorted.length - 1);
            let cursor = first.x + first.width + gap;
            for (let i = 1; i < sorted.length - 1; i++) {
              sorted[i]!.x = cursor;
              cursor += sorted[i]!.width + gap;
            }
          } else {
            const sorted = [...targets].sort((a, b) => a.y - b.y);
            const first = sorted[0]!;
            const last = sorted[sorted.length - 1]!;
            const totalSpan = last.y + last.height - first.y;
            const usedHeight = sorted.reduce((sum, e) => sum + e.height, 0);
            const gap = (totalSpan - usedHeight) / (sorted.length - 1);
            let cursor = first.y + first.height + gap;
            for (let i = 1; i < sorted.length - 1; i++) {
              sorted[i]!.y = cursor;
              cursor += sorted[i]!.height + gap;
            }
          }
        }),

      // ---- Text edit overlay ------------------------------------------

      startTextEdit: (id) =>
        set((s) => {
          s.editingTextId = id;
        }),

      stopTextEdit: () =>
        set((s) => {
          s.editingTextId = null;
        }),

      setViewport: (patch) =>
        set((s) => {
          Object.assign(s.app, patch);
        }),

      commit: () =>
        set((s) => {
          const snapshot: HistoryEntry = { elements: s.elements.map((e) => ({ ...e })) };
          s.history.length = s.historyIndex + 1;
          s.history.push(snapshot);
          if (s.history.length > HISTORY_LIMIT) s.history.shift();
          else s.historyIndex += 1;
        }),

      undo: () => {
        const { history, historyIndex } = get();
        if (historyIndex <= 0) return;
        const target = history[historyIndex - 1];
        if (!target) return;
        set((s) => {
          s.historyIndex -= 1;
          s.elements = target.elements.map((e) => ({ ...e }));
        });
      },

      redo: () => {
        const { history, historyIndex } = get();
        if (historyIndex >= history.length - 1) return;
        const target = history[historyIndex + 1];
        if (!target) return;
        set((s) => {
          s.historyIndex += 1;
          s.elements = target.elements.map((e) => ({ ...e }));
        });
      },

      loadFromJSON: (json) =>
        set((s) => {
          s.elements = json.elements.map((e) => ({ ...e }));
          if (json.appState) Object.assign(s.app, json.appState);
          s.history = [{ elements: s.elements.map((e) => ({ ...e })) }];
          s.historyIndex = 0;
          s.selectedIds = new Set();
        }),

      toJSON: () => {
        const st = get();
        return {
          elements: st.elements.filter((e) => !e.isDeleted).map((e) => ({ ...e })),
          appState: { ...st.app },
        };
      },
    })),
  ),
);

// ---- Element factory helpers -------------------------------------

export const nextIndex = (elements: CanvasElement[]): string => {
  const alive = elements.filter((e) => !e.isDeleted);
  if (alive.length === 0) return midIndex();
  const last = alive[alive.length - 1]!.index;
  return indexBetween(last, null);
};

interface CreateArgs {
  type: ElementType;
  x: number;
  y: number;
  width: number;
  height: number;
  app: AppState;
  elementsForIndex: CanvasElement[];
}

export const createElement = (args: CreateArgs): CanvasElement => {
  const { type, x, y, width, height, app, elementsForIndex } = args;
  const base = {
    id: newId(),
    x,
    y,
    width,
    height,
    angle: 0,
    strokeColor: app.activeStrokeColor,
    fillColor: app.activeFillColor,
    fillStyle: app.activeFillStyle,
    strokeWidth: app.activeStrokeWidth,
    strokeStyle: app.activeStrokeStyle,
    roughness: app.activeRoughness,
    opacity: app.activeOpacity,
    seed: newSeed(),
    version: 1,
    versionNonce: newNonce(),
    isDeleted: false,
    groupIds: [] as string[],
    frameId: null,
    boundElements: [] as Array<{ id: ElementId; type: 'arrow' | 'text' }>,
    link: null,
    locked: false,
    updated: now(),
    index: nextIndex(elementsForIndex),
  };

  switch (type) {
    case 'rect':
      return { ...base, type: 'rect', roundness: null };
    case 'ellipse':
      return { ...base, type: 'ellipse' };
    case 'diamond':
      return { ...base, type: 'diamond', roundness: null };
    case 'line':
      return { ...base, type: 'line', points: [[0, 0], [width, height]] };
    case 'arrow':
      return {
        ...base,
        type: 'arrow',
        points: [[0, 0], [width, height]],
        startBinding: null,
        endBinding: null,
        startArrowhead: null,
        endArrowhead: 'arrow',
        elbowed: false,
      };
    case 'freehand':
      return { ...base, type: 'freehand', points: [[0, 0, 0.5]], simulatePressure: true };
    case 'text':
      return {
        ...base,
        type: 'text',
        text: '',
        fontSize: app.activeFontSize,
        fontFamily: app.activeFontFamily,
        textAlign: 'left',
        verticalAlign: 'top',
        containerId: null,
        originalText: '',
        autoResize: true,
        lineHeight: 1.25,
      };
    case 'sticky':
      return { ...base, type: 'sticky', text: '', colorScheme: 'yellow' };
    case 'frame':
      return { ...base, type: 'frame', name: 'Frame', children: [] };
    default:
      return { ...base, type: 'rect', roundness: null };
  }
};
